import { isEqual, omit } from 'lodash'
import { IShipmentStates, Maybe } from 'types/graphqlTypes'
import { IMissingTrailersQuery } from 'apps/marketplace/pages/shippers/components/ShipperTrailers/graphql/MissingTrailers'
import { IShipperTrailersQuery } from 'apps/marketplace/pages/shippers/components/ShipperTrailers/graphql/ShipperTrailers'

interface IFormatTrailerData {
  trailersData: IShipperTrailersQuery | undefined
  missingTrailersData: IMissingTrailersQuery | undefined
  showMissingTrailers: boolean | undefined
}

/**
 * Returns data from the trailers and shipments queries in the same format.
 */
export const formatTrailerData = ({
  missingTrailersData,
  showMissingTrailers,
  trailersData,
}: IFormatTrailerData) => {
  if (showMissingTrailers) {
    const { shipments } = missingTrailersData || {}
    const { nodes: shipmentNodes } = shipments || {}
    const { pageInfo, totalCount } = shipments || {}
    // Format shipments to match trailer object schema
    const missingTrailerNodes =
      shipmentNodes?.map(shipment => {
        const { carrierApplication, id } = shipment || {}
        return {
          id,
          carrier: carrierApplication,
          shipment: omit(shipment, ['carrierApplication']),
        }
      }) || []

    return {
      data: missingTrailerNodes,
      pageInfo,
      totalCount,
    }
  }

  const { trailers } = trailersData || {}
  const { nodes: knownTrailerNodes } = trailers || {}
  const { pageInfo, totalCount } = trailers || {}
  return {
    data: knownTrailerNodes,
    pageInfo,
    totalCount,
  }
}

export const ON_HOLD_TO_ARRIVED_FOR_DELIVERY_STATES = [
  IShipmentStates.on_hold,
  IShipmentStates.alerted,
  IShipmentStates.tendered,
  IShipmentStates.covered,
  IShipmentStates.offered,
  IShipmentStates.waterfall_offered,
  IShipmentStates.assigned,
  IShipmentStates.dispatched,
  IShipmentStates.arrived_for_pickup,
  IShipmentStates.truck_ordered_not_used,
  IShipmentStates.in_transit,
  IShipmentStates.arrived_for_delivery,
]

export const ON_HOLD_TO_DELIVERED_STATES = ON_HOLD_TO_ARRIVED_FOR_DELIVERY_STATES.concat([
  IShipmentStates.delivered,
])

export const DELIVERED_STATES = Object.freeze<IShipmentStates[]>([
  IShipmentStates.canceled,
  IShipmentStates.completed,
  IShipmentStates.delivered,
  IShipmentStates.documents_received,
  IShipmentStates.invoice_sent,
  IShipmentStates.truck_ordered_not_used,
  IShipmentStates.pod_rejected,
])

/**
 * Converts from a string list to an array
 * @example
 * "123,321,,555,33   ,   ,789"
 * returns ['123', '321', '555', '33', '789']
 */
export const formatStringListToQueryVariable = (stringInput: Maybe<string>): null | string[] => {
  if (!stringInput) return null
  return stringInput
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)
}

/**
 * Gets value of isShipmentDrop based on the selected load types.
 * If none or both types are selected, then isShipmentDrop is undefined, since the trailers query
 * needs this value only if we are querying for one type of load.
 */
export const getIsShipmentDropValue = (loadTypes: ('drop' | 'live')[] | undefined) => {
  if (!loadTypes?.length || isEqual(['drop', 'live'], loadTypes)) return undefined
  return loadTypes?.[0] === 'drop'
}

const updatedAtSortingOptions = [
  {
    label: 'Most Recently Updated',
    value: 'most_recently_updated',
    sortByValue: 'updated_at',
    sortOrderValue: 'DESC',
  },
  {
    label: 'Not Recently Updated',
    value: 'not_recently_updated',
    sortByValue: 'updated_at',
    sortOrderValue: 'ASC',
  },
]

const trailerOnlySortingOptions = [
  {
    label: 'By Trailer Status',
    value: 'by_trailer_status',
    sortByValue: 'state',
    sortOrderValue: 'DESC',
  },
  {
    label: 'Most Recent PU Date',
    value: 'most_recent_pu_date',
    sortByValue: 'pickup_date',
    sortOrderValue: 'DESC',
  },
  {
    label: 'Farthest PU Date',
    value: 'farthest_pu_date',
    sortByValue: 'pickup_date',
    sortOrderValue: 'ASC',
  },
]

/**
 * Gets the appropriate sorting options based on the whether known or missing trailer information is being shown.
 * @param showMissingTrailers
 * @param page the page that these options will be used on (ie. if used on shipper page, then we can sort by carrier)
 */
export const getSortingOptions = (showMissingTrailers: boolean, page: 'carrier' | 'shipper') => {
  const pageSortBy =
    page === 'shipper'
      ? {
          label: 'By Carrier',
          value: 'by_carrier',
          sortByValue: showMissingTrailers ? 'carrier_application_id' : 'carrier_id',
          sortOrderValue: 'DESC',
        }
      : {
          label: 'By Shipper',
          value: 'by_shipper',
          sortByValue: showMissingTrailers ? 'shipper_application_id' : 'shipper_id',
          sortOrderValue: 'DESC',
        }
  const options = [...updatedAtSortingOptions, pageSortBy]
  if (showMissingTrailers) return options
  return [...options, ...trailerOnlySortingOptions]
}
